import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Lock, Check, ChevronLeft, AlertTriangle, PackageCheck, Download, Sparkles } from "lucide-react";
import {
  adminListAssembled, adminGenerateAssembled, adminGetAssembled,
  adminFillAssembled, adminExecuteAssembled, adminHandoffStatus, adminGenerateHandoff,
  adminAssembledPdfUrl,
} from "@/lib/api";

const field = "w-full bg-[#0b0b0c] border border-[#1f1f1f] text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/60";
const DOC_LABELS = { msda_sow: "MSDA + SOW", ip_assignment: "IP Assignment" };
const chip = (st) => ({
  EXECUTED: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  READY: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
  DRAFT: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  BLOCKED: "text-red-300 border-red-400/30 bg-red-400/10",
}[st] || "text-zinc-300 border-[#232323]");

export function AssemblyPanel({ proposalId }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { setDocs((await adminListAssembled(proposalId)).documents || []); }
    catch { setDocs([]); }
  };
  useEffect(() => { load(); }, [proposalId]);

  const generate = async (type) => {
    setBusy(type);
    try {
      const d = await adminGenerateAssembled(proposalId, type);
      toast.success(`${DOC_LABELS[type]} assembled (${d.status}).`);
      await load();
      setOpen(d.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assemble document.");
    } finally { setBusy(""); }
  };

  if (open) return <AssembledDetail docId={open} onBack={() => { setOpen(null); load(); }} />;

  return (
    <div data-testid="assembly-panel">
      <div className="mono-label text-cyan-400 mb-3">LEGAL ASSEMBLY (DETERMINISTIC)</div>
      <div className="flex flex-wrap gap-3 mb-4">
        {["msda_sow", "ip_assignment"].map((t) => (
          <button key={t} data-testid={`assembly-generate-${t}`} onClick={() => generate(t)} disabled={!!busy}
            className="inline-flex items-center gap-2 border border-[#232323] hover:border-cyan-400/50 text-white px-4 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50">
            {busy === t ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-cyan-400" />}
            Assemble {DOC_LABELS[t]}
          </button>
        ))}
      </div>
      {docs.length === 0 && <div className="text-zinc-500 text-sm">No assembled documents yet. Assemble one from the canonical project data above.</div>}
      <div className="space-y-2">
        {docs.map((d) => (
          <button key={d.id} data-testid={`assembly-row-${d.document_type}`} onClick={() => setOpen(d.id)}
            className="w-full flex items-center justify-between border border-[#1f1f1f] hover:border-[#2a2a2a] rounded-lg px-4 py-3 text-left transition-colors">
            <div>
              <div className="text-white text-sm">{DOC_LABELS[d.document_type] || d.document_type} <span className="text-zinc-600">· v{d.source_version}</span></div>
              <div className="text-zinc-500 text-xs mt-0.5">{d.sow_number || "—"} · {d.effective_date}</div>
            </div>
            <span className={`mono-label text-xs px-2.5 py-1 rounded-full border ${chip(d.status)}`}>{d.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssembledDetail({ docId, onBack }) {
  const [d, setD] = useState(null);
  const [fills, setFills] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const x = await adminGetAssembled(docId);
    setD(x);
    setFills(x.filled_fields || {});
  };
  useEffect(() => { load(); }, [docId]);

  if (!d) return <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const saveFills = async () => {
    setBusy(true);
    try { const x = await adminFillAssembled(docId, fills); setD(x); toast.success(`Saved. ${x.pending_fields.length} field(s) still pending.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  const acceptSuggestions = () => {
    const next = { ...fills };
    let n = 0;
    (d.pending_fields || []).forEach((s) => {
      if (!next[s.key] && s.suggestion) { next[s.key] = s.suggestion; n++; }
    });
    setFills(next);
    toast.success(n ? `Applied ${n} suggestion(s). Review, then Save.` : "No suggestions available to apply.");
  };
  const downloadPdf = async () => {
    setBusy(true);
    try { const url = await adminAssembledPdfUrl(docId); window.open(url, "_blank"); }
    catch { toast.error("Could not generate PDF."); }
    finally { setBusy(false); }
  };
  const execute = async () => {
    setBusy(true);
    try { await adminExecuteAssembled(docId); toast.success("Document EXECUTED and locked."); await load(); }
    catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Execution blocked.");
    } finally { setBusy(false); }
  };

  const executed = d.status === "EXECUTED";
  return (
    <div data-testid="assembly-detail">
      <button data-testid="assembly-back" onClick={onBack} className="mono-label text-zinc-400 hover:text-white inline-flex items-center gap-1.5 mb-4"><ChevronLeft className="h-4 w-4" /> Assembled documents</button>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-xl text-white">{DOC_LABELS[d.document_type] || d.document_type}</h3>
          <div className="text-zinc-500 text-xs mt-1">{d.sow_number} · master v{d.source_version} · eff {d.effective_date}</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="assembly-download-pdf" onClick={downloadPdf} disabled={busy}
            className="inline-flex items-center gap-1.5 border border-[#232323] hover:border-cyan-400/50 text-white px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50">
            <Download className="h-3.5 w-3.5 text-cyan-400" /> {d.status === "EXECUTED" ? "Download PDF" : "Preview PDF"}
          </button>
          <span className={`mono-label text-xs px-2.5 py-1 rounded-full border ${chip(d.status)}`}>{d.status}</span>
        </div>
      </div>

      {d.blocked_reason && (
        <div data-testid="assembly-blocked" className="mb-4 flex items-start gap-2 border border-red-400/30 bg-red-400/5 rounded-lg px-4 py-3 text-red-300 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {d.blocked_reason}
        </div>
      )}

      <div className="mb-4">
        <div className="mono-label text-cyan-400 mb-2 text-xs">FIELD PROVENANCE (CANONICAL)</div>
        <div className="grid sm:grid-cols-2 gap-1.5">
          {d.field_provenance.map((p) => (
            <div key={p.field} className="text-xs flex justify-between border-b border-[#141414] py-1">
              <span className="text-zinc-500">{p.field}</span>
              <span className="text-white truncate max-w-[60%]">{String(p.value ?? "—")}</span>
            </div>
          ))}
        </div>
      </div>

      {!executed && d.pending_fields?.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="mono-label text-amber-300 text-xs">
              {d.pending_fields.length} BLANKS TO COMPLETE{d.auto_filled_keys?.length ? ` · ${d.auto_filled_keys.length} AUTO-FILLED FROM SCOPE` : ""} (BLOCKS EXECUTION)
            </div>
            <button data-testid="assembly-accept-suggestions" onClick={acceptSuggestions}
              className="inline-flex items-center gap-1.5 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 px-3 py-1.5 rounded-lg text-xs transition-colors">
              <Sparkles className="h-3.5 w-3.5" /> Accept all suggestions
            </button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {d.pending_fields.map((s) => (
              <div key={s.key} className="grid grid-cols-[110px_1fr] items-center gap-2">
                <span className="text-zinc-500 text-xs truncate" title={s.hint || s.token}>{s.token}</span>
                <input data-testid={`assembly-fill-${s.key}`} className={field}
                  placeholder={s.suggestion || "[TO BE COMPLETED]"}
                  value={fills[s.key] || ""} onChange={(e) => setFills((f) => ({ ...f, [s.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <button data-testid="assembly-save-fills" onClick={saveFills} disabled={busy}
            className="mt-3 inline-flex items-center gap-2 border border-[#232323] hover:border-cyan-400/50 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save completed blanks
          </button>
        </div>
      )}

      <div className="mb-4">
        <div className="mono-label text-cyan-400 mb-2 text-xs">RENDERED DOCUMENT</div>
        <pre data-testid="assembly-rendered" className="whitespace-pre-wrap font-sans text-xs text-zinc-300 leading-relaxed bg-[#0b0b0c] border border-[#1f1f1f] rounded-lg p-4 max-h-96 overflow-y-auto">
          {(executed ? d.executed_snapshot?.rendered_content : d.rendered_content) || ""}
        </pre>
      </div>

      {executed ? (
        <div className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="assembly-executed">
          <Lock className="h-4 w-4" /> Executed {d.executed_at?.slice(0, 10)} · {d.executed_hash?.slice(0, 22)}… (immutable)
        </div>
      ) : (
        <button data-testid="assembly-execute" onClick={execute} disabled={busy || d.status !== "READY"}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full mono-label text-sm hover:bg-[#00b8cc] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Lock className="h-4 w-4" /> Execute &amp; lock
        </button>
      )}
    </div>
  );
}

export function HandoffPanel({ projectId }) {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setH(await adminHandoffStatus(projectId)); } catch { setH(null); } };
  useEffect(() => { load(); }, [projectId]);
  if (!h) return null;

  const generate = async () => {
    setBusy(true);
    try { await adminGenerateHandoff(projectId); toast.success("Handoff package generated."); await load(); }
    catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || (typeof det === "string" ? det : "Handoff blocked."));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="handoff-panel" className="mt-8">
      <div className="mono-label text-cyan-400 mb-3">CLIENT HANDOFF PACKAGE (PHASE 5)</div>
      <div className="space-y-1.5 mb-4">
        {h.conditions.map((c) => (
          <div key={c.key} className="flex items-center gap-2 text-sm">
            {c.met ? <Check className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
            <span className={c.met ? "text-zinc-300" : "text-zinc-500"}>{c.label}</span>
          </div>
        ))}
      </div>
      {h.package ? (
        <div data-testid="handoff-generated" className="flex items-start gap-2 border border-emerald-400/30 bg-emerald-400/5 rounded-lg px-4 py-3 text-emerald-300 text-sm">
          <PackageCheck className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Handoff package generated {h.package.generated_at?.slice(0, 10)}.<br />
            <span className="text-zinc-400 text-xs">{(h.package.executed_documents || []).map((d) => d.document_type).join(", ")} · {h.package.package_hash?.slice(0, 22)}…</span>
          </div>
        </div>
      ) : (
        <button data-testid="handoff-generate" onClick={generate} disabled={busy || !h.ready}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full mono-label text-sm hover:bg-[#00b8cc] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Generate handoff package
        </button>
      )}
    </div>
  );
}
